/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { fetchGraphQl } from '@/cart/api';
import { handleFetchError } from '@/cart/lib/fetch-error';
import { state } from '@/cart/lib/state';

import {
  transformPartialAddress,
  transformShippingMethod,
} from '@/cart/data/transforms/transform-shipping';
import { events } from '@adobe-commerce/event-bus';
import {
  COUNTRIES_QUERY,
  CountryData,
  REGIONS_QUERY,
} from './graphql/countriesAndRegionsQueries';
import { ESTIMATE_SHIPPING_METHODS_MUTATION } from './graphql/estimateShippingMethodsMutation';

export interface EstimateAddressInput {
  countryCode: string;
  postcode?: string;
  region?: {
    region?: string;
    code?: string;
    id?: number;
  };
}

export const getEstimateShipping = async (
  address: EstimateAddressInput
): Promise<any | null> => {
  const cartId = state.cartId;

  if (!cartId) throw new Error('No cart ID found');
  if (!address) throw new Error('No address parameter found');

  const { countryCode, postcode, region } = address;

  const addressInput = {
    country_code: countryCode || 'US',
    postcode: postcode || '',
    region: {
      region: region?.region || '',
      region_id: region?.id,
    },
  };

  return fetchGraphQl(ESTIMATE_SHIPPING_METHODS_MUTATION, {
    variables: {
      cartId,
      address: addressInput,
    },
  }).then(({ errors, data }) => {
    if (errors) return handleFetchError(errors);

    const estimateShippingMethods: Array<any> = data.estimateShippingMethods;

    const selectedMethod =
      estimateShippingMethods.find((method) => !method.error_message) || null;

    events.emit('shipping/estimate', {
      address: transformPartialAddress(addressInput),
      shippingMethod: transformShippingMethod(selectedMethod),
    });

    return selectedMethod;
  });
};

export const getCountries = async (): Promise<[CountryData]> => {
  return fetchGraphQl(COUNTRIES_QUERY, { method: 'GET' }).then(
    ({ errors, data }) => {
      if (errors) return handleFetchError(errors);

      const sortedCountries =
        data?.countries?.sort((a: any, b: any) =>
          a.label.localeCompare(b.label)
        ) || [];

      const defaultCountry = data?.storeConfig?.defaultCountry || 'US';

      // Set the default country property
      sortedCountries.forEach((country: any) => {
        country.isDefaultCountry = country.id === defaultCountry;
      });

      return sortedCountries;
    }
  );
};

export const getRegions = async (
  countryId: string
): Promise<Array<{ code: string; name: string }>> => {
  return fetchGraphQl(REGIONS_QUERY, {
    method: 'GET',
    variables: {
      id: countryId,
    },
  }).then(({ errors, data }) => {
    if (errors) return handleFetchError(errors);

    return data?.country?.available_regions || [];
  });
};
